import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Navbar from './components/Navbar';
import {
  breadcrumbJsonLd,
  currentCanonical,
  organizationJsonLd,
  setJsonLd,
  setPageMeta,
  trackVisit,
  websiteJsonLd
} from './utils/seo_geo';

// 懒加载页面组件
const Home = React.lazy(() => import('./pages/Home'));
const AIJobRadar = React.lazy(() => import('./pages/AIJobRadar'));
const Services = React.lazy(() => import('./pages/Services'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const News = React.lazy(() => import('./pages/news'));
const NewsDetail = React.lazy(() => import('./pages/news_detail'));
const Career = React.lazy(() => import('./pages/careers'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const DeliveryPreview = React.lazy(() => import('./pages/DeliveryPreview'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

export default function App() {
  const location = useLocation();

  useEffect(() => {
    // 页面切换动画可以在这里实现
    document.documentElement.style.scrollBehavior = 'smooth';
  }, [location.pathname]);

  useEffect(() => {
    const pageNames: Record<string, string> = {
      '/': '首页',
      '/services': 'AI服务',
      '/about': '关于我们',
      '/contact': '联系我们',
      '/news': '资讯',
      '/careers': '加入我们',
      '/ai-job-radar': 'AI求职雷达',
    };
    const name = pageNames[location.pathname] || (location.pathname.startsWith('/news/') ? '资讯详情' : '山间码坊');
    const description = '山间码坊提供 AI Agent、AI 自动化、软件开发、招聘信息监控和小程序内容运营服务。';
    setPageMeta({
      title: name,
      description,
      keywords: ['山间码坊', 'AI服务', 'AI Agent', '招聘信息监控', 'AI自动化']
    });
    setJsonLd('ld-organization', organizationJsonLd());
    setJsonLd('ld-website', websiteJsonLd());
    setJsonLd('ld-breadcrumb', breadcrumbJsonLd([
      { name: '首页', url: currentCanonical('/') },
      { name, url: currentCanonical(location.pathname) }
    ]));
    trackVisit(location.pathname, name);
  }, [location.pathname]);

  // 加载状态组件
  const Loading = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh',
      fontSize: '1.2rem',
      color: '#6b7280'
    }}>
      加载中...
    </div>
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <Navbar />
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/ai-job-radar" element={<AIJobRadar />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:id" element={<NewsDetail />} />
          <Route path="/careers" element={<Career />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/view/:id" element={<DeliveryPreview />} />
        </Routes>
      </Suspense>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </div>
  );
}
